import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Plus, Minus, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const CYCLE_START = new Date(2026, 2, 30);
const getMenuWeekForDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  const diffMs = d - CYCLE_START;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weekOffset = Math.floor(diffDays / 7);
  return ((weekOffset % 3) + 3) % 3 + 1;
};

// Extracted outside the main component to prevent remounting on state changes
const ProductRow = React.memo(({ product, categoryName, qty, onUpdateQty, onInputQty }) => (
  <div className="flex items-center justify-between py-2 px-3 hover:bg-slate-50 rounded-lg">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-900 truncate">{product.name}</p>
      <p className="text-xs text-slate-500">{categoryName}</p>
    </div>
    <div className="flex items-center gap-2 ml-3">
      <Button variant="outline" size="icon" className="h-7 w-7 rounded-full"
        onClick={() => onUpdateQty(product.id, -1)} disabled={qty === 0}
        data-testid={`product-minus-${product.id}`}>
        <Minus className="w-3 h-3" />
      </Button>
      <input
        type="number"
        min="0"
        value={qty}
        onChange={(e) => onInputQty(product.id, e.target.value)}
        className="w-12 text-center text-sm font-medium border border-slate-200 rounded-md h-7 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        style={{ color: qty > 0 ? '#15803d' : '#94a3b8' }}
        data-testid={`product-qty-${product.id}`}
      />
      <Button variant="outline" size="icon" className="h-7 w-7 rounded-full"
        onClick={() => onUpdateQty(product.id, 1)}
        data-testid={`product-plus-${product.id}`}>
        <Plus className="w-3 h-3" />
      </Button>
    </div>
  </div>
));
ProductRow.displayName = 'ProductRow';

const AddonRow = React.memo(({ name, qty, onUpdateQty, onInputQty }) => (
  <div className="flex items-center justify-between py-2 px-3 hover:bg-slate-50 rounded-lg">
    <p className="text-sm font-medium text-slate-900">{name}</p>
    <div className="flex items-center gap-2 ml-3">
      <Button variant="outline" size="icon" className="h-7 w-7 rounded-full"
        onClick={() => onUpdateQty(name, -1)} disabled={qty === 0}
        data-testid={`addon-minus-${name}`}>
        <Minus className="w-3 h-3" />
      </Button>
      <input
        type="number"
        min="0"
        value={qty}
        onChange={(e) => onInputQty(name, e.target.value)}
        className="w-12 text-center text-sm font-medium border border-slate-200 rounded-md h-7 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        style={{ color: qty > 0 ? '#15803d' : '#94a3b8' }}
        data-testid={`addon-qty-${name}`}
      />
      <Button variant="outline" size="icon" className="h-7 w-7 rounded-full"
        onClick={() => onUpdateQty(name, 1)}
        data-testid={`addon-plus-${name}`}>
        <Plus className="w-3 h-3" />
      </Button>
    </div>
  </div>
));
AddonRow.displayName = 'AddonRow';

const AdminOrderEditor = ({ open, onClose, order, onSaved, clients }) => {
  const isEditing = !!order;
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [addons, setAddons] = useState({ basic: [], premium: [] });
  const [quantities, setQuantities] = useState({});
  const [addonQuantities, setAddonQuantities] = useState({});
  const [selectedClient, setSelectedClient] = useState('');
  const [deliveryWeek, setDeliveryWeek] = useState('');
  const [menuWeek, setMenuWeek] = useState(null);
  const [notes, setNotes] = useState('');
  const [addonPreference, setAddonPreference] = useState('basic');
  const [searchQuery, setSearchQuery] = useState('');
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    fetchProductData();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || loading) return;
    if (isEditing && order) {
      setDeliveryWeek(order.delivery_week || '');
      setMenuWeek(order.menu_week || null);
      setNotes(order.notes || '');
      setAddonPreference(order.addon_preference || 'basic');
      const qMap = {};
      (order.items || []).forEach(item => {
        if (item.product_id) qMap[item.product_id] = item.quantity;
      });
      setQuantities(qMap);
      const aMap = {};
      (order.addon_selections || []).forEach(sel => {
        aMap[sel.addon_name] = sel.quantity;
      });
      setAddonQuantities(aMap);
    } else {
      setQuantities({});
      setAddonQuantities({});
      setNotes('');
      setAddonPreference('basic');
      setSelectedClient('');
      setDeliveryWeek('');
      setMenuWeek(null);
    }
  }, [open, loading, order, isEditing]);

  const fetchProductData = async () => {
    setLoading(true);
    try {
      const [catRes, prodRes, addonRes] = await Promise.all([
        axios.get(`${API_URL}/api/categories`, { withCredentials: true }),
        axios.get(`${API_URL}/api/products/all`, { withCredentials: true }),
        axios.get(`${API_URL}/api/addons`, { withCredentials: true }),
      ]);
      setCategories(catRes.data);
      setProducts(prodRes.data);
      setAddons(addonRes.data);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleDeliveryWeekChange = (val) => {
    setDeliveryWeek(val);
    if (val) setMenuWeek(getMenuWeekForDate(val));
    else setMenuWeek(null);
  };

  const getFilteredProducts = (deliveryDay) => {
    return products.filter(p => {
      const cat = categories.find(c => c.id === p.category_id);
      if (!cat) return false;
      if (cat.delivery_day !== deliveryDay && cat.meal_type !== deliveryDay) return false;
      if (cat.rotation_type === 'Rotating' && menuWeek && !p.menu_weeks?.includes(menuWeek)) return false;
      if (searchQuery) {
        return p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
               cat.name.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return true;
    });
  };

  const mondayProducts = useMemo(() => getFilteredProducts('Monday'), [products, categories, menuWeek, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  const wednesdayProducts = useMemo(() => getFilteredProducts('Wednesday'), [products, categories, menuWeek, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Combine basic + premium add-ons, filtered by search
  const allAddonNames = useMemo(() => {
    const combined = [
      ...(addons.basic || []).map(name => ({ name, type: 'basic' })),
      ...(addons.premium || []).map(name => ({ name, type: 'premium' })),
    ];
    if (!searchQuery) return combined;
    return combined.filter(a => a.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [addons, searchQuery]);

  const updateQty = useCallback((productId, delta) => {
    setQuantities(prev => {
      const current = prev[productId] || 0;
      const next = Math.max(0, current + delta);
      const copy = { ...prev };
      if (next === 0) delete copy[productId];
      else copy[productId] = next;
      return copy;
    });
  }, []);

  const inputQty = useCallback((productId, value) => {
    const parsed = parseInt(value, 10);
    setQuantities(prev => {
      const copy = { ...prev };
      if (isNaN(parsed) || parsed <= 0) delete copy[productId];
      else copy[productId] = parsed;
      return copy;
    });
  }, []);

  const updateAddonQty = useCallback((name, delta) => {
    setAddonQuantities(prev => {
      const current = prev[name] || 0;
      const next = Math.max(0, current + delta);
      const copy = { ...prev };
      if (next === 0) delete copy[name];
      else copy[name] = next;
      return copy;
    });
  }, []);

  const inputAddonQty = useCallback((name, value) => {
    const parsed = parseInt(value, 10);
    setAddonQuantities(prev => {
      const copy = { ...prev };
      if (isNaN(parsed) || parsed <= 0) delete copy[name];
      else copy[name] = parsed;
      return copy;
    });
  }, []);

  const buildItems = () => {
    const items = [];
    Object.entries(quantities).forEach(([productId, qty]) => {
      if (qty <= 0) return;
      const product = products.find(p => p.id === productId);
      if (!product) return;
      const cat = categories.find(c => c.id === product.category_id);
      items.push({
        product_id: productId,
        product_name: product.name,
        quantity: qty,
        category_name: cat?.name || '',
        meal_type_tag: product.meal_type_tag || cat?.meal_type || '',
        delivery_day: cat?.delivery_day || 'Monday',
      });
    });
    return items;
  };

  const buildAddonSelections = () => {
    return Object.entries(addonQuantities)
      .filter(([, qty]) => qty > 0)
      .map(([name, qty]) => ({
        addon_name: name,
        addon_type: (addons.premium || []).includes(name) ? 'premium' : 'basic',
        quantity: qty,
        is_additional: false,
      }));
  };

  const totalItems = Object.values(quantities).reduce((sum, q) => sum + q, 0);
  const totalAddons = Object.values(addonQuantities).reduce((sum, q) => sum + q, 0);

  const handleSubmit = async () => {
    if (totalItems === 0) {
      toast.error('Please add at least one item');
      return;
    }
    if (!deliveryWeek) {
      toast.error('Please select a delivery week');
      return;
    }
    if (!isEditing && !selectedClient) {
      toast.error('Please select a client');
      return;
    }

    setSubmitting(true);
    try {
      const items = buildItems();
      const addonSels = buildAddonSelections();

      if (isEditing) {
        await axios.put(`${API_URL}/api/admin/orders/${order.id}/edit`, {
          delivery_week: deliveryWeek,
          menu_week: menuWeek,
          items,
          addon_selections: addonSels,
          addon_preference: addonPreference,
          notes,
        }, { withCredentials: true });
        toast.success('Order updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/orders/create`, {
          user_id: selectedClient,
          delivery_week: deliveryWeek,
          menu_week: menuWeek,
          items,
          addon_selections: addonSels,
          addon_preference: addonPreference,
          notes,
        }, { withCredentials: true });
        toast.success('Order created successfully');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save order');
    } finally {
      setSubmitting(false);
    }
  };

  // Build a category name lookup once
  const catNameMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.id] = c.name; });
    return map;
  }, [categories]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="admin-order-editor-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isEditing ? 'Edit Order' : 'Create Order for Client'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 space-y-6 pr-1" ref={scrollContainerRef}>
            {/* Client Selector (Create mode only) */}
            {!isEditing && (
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger className="rounded-xl" data-testid="admin-order-client-select">
                    <SelectValue placeholder="Select a client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(clients || []).map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.organisation_name || c.name} ({c.username})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Order info */}
            {isEditing && (
              <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-600">
                <span className="font-medium">{order.organisation_name}</span> — {order.user_email}
              </div>
            )}

            {/* Delivery Week */}
            <div className="space-y-2">
              <Label>Delivery Week (Monday) *</Label>
              <Input type="date" value={deliveryWeek} onChange={e => handleDeliveryWeekChange(e.target.value)}
                className="rounded-xl" data-testid="admin-order-delivery-week" />
              {menuWeek && <Badge className="bg-green-100 text-green-700">Menu Week {menuWeek}</Badge>}
            </div>

            {/* Product Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search products & add-ons..." value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)} className="pl-10 rounded-xl"
                data-testid="admin-order-search" />
            </div>

            {/* Monday Products */}
            {mondayProducts.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-slate-900">Monday Delivery ({mondayProducts.length})</h4>
                <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-48 overflow-y-auto">
                  {mondayProducts.map(p => (
                    <ProductRow
                      key={p.id}
                      product={p}
                      categoryName={catNameMap[p.category_id] || ''}
                      qty={quantities[p.id] || 0}
                      onUpdateQty={updateQty}
                      onInputQty={inputQty}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Wednesday Products */}
            {wednesdayProducts.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-slate-900">Wednesday Delivery ({wednesdayProducts.length})</h4>
                <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-48 overflow-y-auto">
                  {wednesdayProducts.map(p => (
                    <ProductRow
                      key={p.id}
                      product={p}
                      categoryName={catNameMap[p.category_id] || ''}
                      qty={quantities[p.id] || 0}
                      onUpdateQty={updateQty}
                      onInputQty={inputQty}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Add-ons Section (fetched from /api/addons) */}
            {allAddonNames.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-slate-900">Add-ons ({allAddonNames.length})</h4>
                <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-52 overflow-y-auto">
                  {allAddonNames.map(addon => (
                    <AddonRow
                      key={addon.name}
                      name={addon.name}
                      qty={addonQuantities[addon.name] || 0}
                      onUpdateQty={updateAddonQty}
                      onInputQty={inputAddonQty}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Allergy & Dietary Notes</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Any allergy or dietary notes..." className="rounded-xl" rows={2}
                data-testid="admin-order-notes" />
            </div>

            {/* Summary */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
              <span className="text-sm font-medium text-green-800" data-testid="admin-order-item-count">
                {totalItems} item{totalItems !== 1 ? 's' : ''} selected
              </span>
              {totalAddons > 0 && (
                <span className="text-xs text-green-600" data-testid="admin-order-addon-count">
                  {totalAddons} add-on(s)
                </span>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="admin-order-cancel">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || loading}
            className="rounded-full bg-green-600 hover:bg-green-700" data-testid="admin-order-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              : isEditing ? 'Save Changes' : 'Create Order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminOrderEditor;
